// ============================================================================
// OOM EXCEL PARSER
// Parses FSGU OOM Excel format for import
// Extracts data exactly as provided - no calculations or reordering
// ============================================================================

import * as XLSX from 'xlsx';

/**
 * Parse FSGU OOM Excel file
 * @param {File} file - Excel file
 * @returns {Promise<Object>} Parsed data ready for API
 */
export const parseOOMExcel = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        const sheets = [];

        workbook.SheetNames.forEach((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });

          // Find header row (contains "Rank" and "Player Name")
          let headerRowIndex = -1;
          let headers = [];

          for (let i = 0; i < Math.min(10, jsonData.length); i++) {
            const row = jsonData[i];
            if (row && Array.isArray(row)) {
              const rowStr = row.map((c) => String(c || '').toLowerCase()).join(' ');
              if (rowStr.includes('rank') && rowStr.includes('player')) {
                headerRowIndex = i;
                headers = row;
                break;
              }
            }
          }

          if (headerRowIndex === -1) {
            console.warn(`Could not find header row in sheet: ${sheetName}`);
            return;
          }

          // Map column indices
          const colMap = mapColumns(headers);

          if (colMap.playerName === -1) {
            console.warn(`Could not find Player Name column in sheet: ${sheetName}`);
            return;
          }

          // Parse data rows - maintain exact order from Excel
          const rows = [];
          for (let i = headerRowIndex + 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || !row[colMap.playerName]) continue;

            const playerName = String(row[colMap.playerName] || '').trim();
            if (!playerName || playerName.toLowerCase() === 'rank') continue;

            const parsedRow = {
              // Position from their Rank column, or fallback to row order
              position: colMap.rank !== -1 && row[colMap.rank] != null
                ? parseInt(row[colMap.rank])
                : rows.length + 1,
              player_name: playerName,
              club: colMap.club !== -1 ? String(row[colMap.club] || '').trim() || null : null,
              // Summary columns - extracted as-is
              oom_total_pts: colMap.oomTotal !== -1 ? parseNum(row[colMap.oomTotal]) : null,
              best_10_pts: colMap.best10 !== -1 ? parseNum(row[colMap.best10]) : null,
              fs_rounds: colMap.fsRounds !== -1 ? parseNum(row[colMap.fsRounds]) : null,
              total_rounds: colMap.totalRounds !== -1 ? parseNum(row[colMap.totalRounds]) : null,
            };

            // Parse event scores
            colMap.events.forEach((eventCol) => {
              const value = row[eventCol.index];
              if (value !== null && value !== undefined && value !== '' && !isNaN(value)) {
                parsedRow[eventCol.key] = parseFloat(value);
              }
            });

            // Parse bonus points
            colMap.bonusPoints.forEach((bpCol) => {
              const value = row[bpCol.index];
              if (value !== null && value !== undefined && value !== '' && !isNaN(value)) {
                parsedRow[bpCol.key] = parseFloat(value);
              }
            });

            rows.push(parsedRow);
          }

          if (rows.length > 0) {
            sheets.push({
              name: sheetName,
              rows: rows,
              summary: {
                players: rows.length,
                events: colMap.events.length,
                bonusColumns: colMap.bonusPoints.length,
              },
            });
          }
        });

        resolve({
          success: true,
          sheets: sheets,
          fileName: file.name,
        });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Safe number parser - returns null if not a number
 */
function parseNum(val) {
  if (val === null || val === undefined || val === '') return null;
  const n = parseFloat(val);
  return isNaN(n) ? null : n;
}

/**
 * Map column headers to indices
 */
function mapColumns(headers) {
  const colMap = {
    rank: -1,
    playerName: -1,
    club: -1,
    oomTotal: -1,
    best10: -1,
    fsRounds: -1,
    totalRounds: -1,
    events: [],
    bonusPoints: [],
  };

  headers.forEach((header, index) => {
    if (header === null || header === undefined) return;

    const h = String(header).toLowerCase().trim();

    if (h === 'rank' || h === 'position') {
      colMap.rank = index;
    } else if (h === 'player name' || h === 'player' || h === 'name') {
      colMap.playerName = index;
    } else if (h === 'club') {
      colMap.club = index;
    } else if (h.includes('oom total')) {
      colMap.oomTotal = index;
    } else if (h.includes('10 best') || h.includes('best') && h.includes('rounds')) {
      colMap.best10 = index;
    } else if (h === 'fs rounds played' || h === 'fs rounds') {
      colMap.fsRounds = index;
    } else if (h === 'total rounds') {
      colMap.totalRounds = index;
    } else if (/^bp\s?\d+$/i.test(h)) {
      const match = h.match(/^bp\s?(\d+)$/i);
      if (match) {
        colMap.bonusPoints.push({
          index: index,
          number: parseInt(match[1]),
          key: `bp_${match[1]}`,
          header: h.toUpperCase().replace(/\s/g, ' '),
        });
      }
    } else if (/^\d+(\.0)?$/.test(String(header))) {
      const eventNum = parseInt(header);
      colMap.events.push({
        index: index,
        number: eventNum,
        key: `e_${eventNum}`,
        header: String(eventNum),
      });
    }
  });

  colMap.events.sort((a, b) => a.number - b.number);
  colMap.bonusPoints.sort((a, b) => a.number - b.number);

  return colMap;
}

/**
 * Detect year from filename or content
 */
export const detectYear = (fileName, sheets) => {
  const yearMatch = fileName.match(/20\d{2}/);
  if (yearMatch) {
    return parseInt(yearMatch[0]);
  }

  for (const sheet of sheets) {
    if (sheet.rows && sheet.rows[0]) {
      const firstRow = Object.values(sheet.rows[0]).join(' ');
      const match = firstRow.match(/20\d{2}/);
      if (match) {
        return parseInt(match[0]);
      }
    }
  }

  return new Date().getFullYear();
};

/**
 * Import parsed data to API
 */
export const importOOMData = async (apiBaseUrl, year, sheets, replaceExisting = true) => {
  const response = await fetch(`${apiBaseUrl}/import-oom-excel.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      year: year,
      sheets: sheets,
      replace_existing: replaceExisting,
    }),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.message || 'Import failed');
  }

  return data;
};